import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError, thumb } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, Clock, Sparkles, ArrowRight, KeyRound, Package, ListChecks, Wrench, ShoppingBag, PlayCircle } from "lucide-react";
import { trackViewContent, trackInitiateCheckout } from "../lib/metaPixel";

// Extract a Vimeo video id from a URL or embed code and build the player src.
function vimeoEmbedSrc(raw) {
  if (!raw) return null;
  const s = String(raw);
  const m = s.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([0-9a-zA-Z]+))?/) || s.match(/player\.vimeo\.com\/video\/(\d+)(?:\?h=([0-9a-zA-Z]+))?/);
  if (m) {
    const id = m[1];
    const h = m[2];
    return `https://player.vimeo.com/video/${id}${h ? `?h=${h}` : ""}`;
  }
  // Fallback: pull src out of a pasted <iframe ...> embed
  const src = s.match(/src=["']([^"']+)["']/);
  if (src && src[1].includes("vimeo")) return src[1];
  return null;
}

// Read the aspect ratio (height/width as a %) from a pasted responsive embed's padding.
function paddingAspect(raw) {
  const m = String(raw || "").match(/padding\s*:\s*([\d.]+)%/);
  return m ? parseFloat(m[1]) : null;
}

const Lines = ({ text }) => (
  <ul className="mt-3 space-y-1.5">
    {String(text).split("\n").map((l) => l.trim()).filter(Boolean).map((l, i) => (
      <li key={i} className="text-[#121212]/80 leading-relaxed flex gap-2">
        <span className="text-[#E0218A] font-bold">•</span><span>{l.replace(/^[-•*]\s*/, "")}</span>
      </li>
    ))}
  </ul>
);

export default function TutorialDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [related, setRelated] = useState([]);
  const [aspectPct, setAspectPct] = useState(56.25); // height/width %, defaults to 16:9
  const [busy, setBusy] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [kitCode, setKitCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [params] = useSearchParams();
  const fromKit = params.get("kit") === "1";
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => {
    api.get(`/tutorials/${id}`).then(({ data }) => {
      setT(data);
      if (data?.slug && data.slug !== id) nav(`/tutorials/${data.slug}${window.location.search}`, { replace: true });
      if (data) trackViewContent({ id: data.id, name: data.title, price: data.price });
    }).catch(() => {
      api.get(`/tutorials`).then(({ data }) => {
        const found = data.find((x) => x.id === id || x.slug === id);
        if (found) setT({ ...found, locked: true, access_type: found.access_type || "vault" });
      });
    });
  };

  useEffect(load, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect the video's true aspect ratio so portrait & landscape both fit responsively.
  useEffect(() => {
    if (!t?.video_url) return;
    const fromPadding = paddingAspect(t.video_url);
    if (fromPadding) { setAspectPct(fromPadding); return; }
    const src = vimeoEmbedSrc(t.video_url);
    if (!src) return;
    fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(src)}`)
      .then((r) => r.json())
      .then((d) => { if (d?.width && d?.height) setAspectPct((d.height / d.width) * 100); })
      .catch(() => {});
  }, [t?.video_url]);

  useEffect(() => {
    if (!t?.id) return;
    api.get("/tutorials").then(({ data }) => {
      const others = (data || []).filter((x) => x.id !== t.id);
      const sameLevel = others.filter((x) => x.level === t.level);
      const pool = [...sameLevel, ...others.filter((x) => x.level !== t.level)];
      const seen = new Set();
      setRelated(pool.filter((x) => (seen.has(x.id) ? false : seen.add(x.id))).slice(0, 3));
    }).catch(() => setRelated([]));
  }, [t?.id, t?.level]);

  const redeem = async () => {
    if (!kitCode.trim()) { toast.error("Enter the access code from inside your kit."); return; }
    if (!user || user === false) {
      toast.message("Create an account or log in to unlock your kit tutorial.");
      nav(`/login?next=${encodeURIComponent(`/tutorials/${id}?kit=1`)}`);
      return;
    }
    setRedeeming(true);
    try {
      await api.post("/kit-codes/redeem", { code: kitCode.trim().toUpperCase() });
      toast.success("Unlocked! Your tutorial is saved to your account.");
      setKitCode("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRedeeming(false);
    }
  };

  const buy = async () => {
    if (!user || user === false) { nav("/login?next=" + encodeURIComponent(`/tutorials/${id}`)); return; }
    setBusy(true);
    trackInitiateCheckout({ id: t.id, name: t.title, value: t.price });
    try {
      const payload = { item_type: "tutorial", item_id: t.id, origin_url: window.location.origin };
      if (giftCode.trim()) payload.gift_code = giftCode.trim().toUpperCase();
      const { data } = await api.post("/payments/checkout", payload);
      if (data.free && data.session_id) { nav(`/payment/success?session_id=${data.session_id}`); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (!t) return <div className="pt-32 px-6 text-stone-500">Loading…</div>;

  const accessType = t.access_type || "vault";
  const isVault = accessType === "vault";
  const embedSrc = vimeoEmbedSrc(t.video_url);
  const shopHref = t.kit_id ? `/shop?kit=${t.kit_id}` : "/shop";
  const isPortrait = aspectPct > 110;

  return (
    <div data-testid="tutorial-detail-page" className="pt-32 pb-24 fade-in bg-[#F2E9D8]">
      <div className="max-w-6xl mx-auto px-6 sm:px-12 lg:px-16">
        <div className={isPortrait ? "flex flex-col lg:flex-row gap-10 lg:items-start" : "grid lg:grid-cols-3 gap-12"}>
          {/* Video / embed */}
          <div className={isPortrait ? "w-full lg:w-[440px] lg:shrink-0" : "lg:col-span-2"}>
            <div
              className="relative overflow-hidden bg-stone-900 border-2 border-[#121212] mx-auto w-full"
              style={{ maxWidth: isPortrait ? 440 : "100%" }}
            >
              <div className="relative w-full" style={{ paddingTop: `${aspectPct}%` }}>
                {t.locked || !embedSrc ? (
                  <>
                    <img src={t.image_url} alt={t.title} className="absolute inset-0 w-full h-full object-cover opacity-50" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white text-center px-6">
                      <div className="w-16 h-16 bg-white/10 backdrop-blur border border-white/30 flex items-center justify-center">
                        <Lock size={24} />
                      </div>
                      <p className="font-serif text-2xl">{t.locked ? (isVault ? "Members-only Vault title" : "Unlock to watch") : "Video coming soon"}</p>
                    </div>
                  </>
                ) : (
                  <iframe
                    data-testid="tutorial-video"
                    src={embedSrc}
                    title={t.title}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                    allowFullScreen
                  />
                )}
              </div>
            </div>
          </div>
          <div className={isPortrait ? "flex-1 min-w-0" : ""}>
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212]">{t.level}</p>
            <h1 className="font-serif font-black text-4xl text-[#121212] mt-3 leading-tight">{t.title}</h1>
            <p className="mt-4 text-[#121212]/60 inline-flex items-center gap-2"><Clock size={16} /> {t.duration}</p>
            <p className="mt-6 text-[#121212]/80 leading-relaxed">{t.description}</p>

            {!t.locked ? (
              <div className="mt-8 p-6 bg-[#C6FF33] border-2 border-[#121212]">
                <p className="font-serif font-black text-2xl text-[#121212] inline-flex items-center gap-2"><Sparkles size={20} /> Ready to watch</p>
                <p className="text-[#121212]/80 text-sm mt-1">
                  {t.member_unlocked ? "Included with your membership — stream it anytime." : "You have access to this tutorial."}
                </p>
              </div>
            ) : (
              <div className="mt-8 space-y-4" data-testid="tutorial-access-options">
                <div data-testid="kit-access-panel" className={`p-6 border-2 border-[#121212] ${fromKit ? "bg-[#C6FF33]" : "bg-white"}`}>
                  <p className="font-serif font-black text-2xl text-[#121212] inline-flex items-center gap-2"><Package size={20} /> Bought the Cake Kit?</p>
                  <p className="text-[#121212]/70 text-sm mt-1">Enter the access code from inside your kit to unlock this tutorial — free, saved to your account forever.</p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <input data-testid="kit-code-input" value={kitCode} autoFocus={fromKit} onChange={(e) => setKitCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && redeem()} placeholder="KIT-XXXX-XXXX" className="flex-1 px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none uppercase font-mono text-sm" />
                    <button data-testid="redeem-kit-code-btn" onClick={redeem} disabled={redeeming} className="px-6 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold transition-all inline-flex items-center justify-center gap-2">
                      <KeyRound size={14} /> {redeeming ? "Unlocking…" : "Unlock"}
                    </button>
                  </div>
                </div>
                {isVault && (
                  <div data-testid="vault-locked-panel" className="p-6 bg-[#121212] text-[#F2E9D8] border-2 border-[#121212]">
                    <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33]">Cake Society Membership</p>
                    <p className="font-serif font-black text-2xl mt-2 leading-tight">Included with Membership.</p>
                    <p className="text-[#F2E9D8]/75 text-sm mt-2">Members stream this and the whole Vault — plus a free monthly studio visit and 15% savings.</p>
                    <Link to="/membership" data-testid="vault-detail-join-btn" className="mt-5 w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#C6FF33] text-[#121212] hover:bg-[#b6ef23] text-xs tracking-[0.25em] uppercase font-bold transition-all">
                      Become a member <ArrowRight size={16} />
                    </Link>
                  </div>
                )}
                {t.price > 0 && (
                  <div data-testid="tutorial-buy-panel" className="p-6 border-2 border-[#121212] bg-white">
                    <p className="font-serif font-black text-3xl text-[#121212]">${t.price}</p>
                    <p className="text-xs tracking-[0.2em] uppercase font-bold mt-1">Buy this tutorial · lifetime access</p>
                    <input data-testid="tutorial-gift-code" value={giftCode} onChange={(e) => setGiftCode(e.target.value)} placeholder="Gift code (optional)" className="mt-4 w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none uppercase font-mono text-sm" />
                    <button data-testid="buy-tutorial-btn" onClick={buy} disabled={busy} className="mt-4 w-full px-6 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold transition-all">
                      {busy ? "Loading…" : "Buy this tutorial"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content sections */}
        <div className="mt-14 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            {t.steps && (
              <section data-testid="tutorial-steps">
                <h2 className="font-serif font-black text-2xl text-[#121212] inline-flex items-center gap-2"><ListChecks size={22} className="text-[#E0218A]" /> Steps &amp; notes</h2>
                <Lines text={t.steps} />
              </section>
            )}
            {t.troubleshooting && (
              <section data-testid="tutorial-troubleshooting">
                <h2 className="font-serif font-black text-2xl text-[#121212] inline-flex items-center gap-2"><Wrench size={22} className="text-[#E0218A]" /> Troubleshooting</h2>
                <Lines text={t.troubleshooting} />
              </section>
            )}
          </div>
          <div className="space-y-6">
            {t.materials && (
              <section data-testid="tutorial-materials" className="p-6 bg-white border-2 border-[#121212]">
                <h2 className="font-serif font-black text-xl text-[#121212]">Supplies &amp; materials</h2>
                <Lines text={t.materials} />
              </section>
            )}
            <Link to={shopHref} data-testid="tutorial-shop-btn" className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all">
              <ShoppingBag size={16} /> {t.kit_id ? "Shop this kit" : "Shop kits"}
            </Link>
          </div>
        </div>

        {related.length > 0 && (
          <section data-testid="related-tutorials" className="mt-20 pt-14 border-t-2 border-[#121212]">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
              <div>
                <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-2">Keep going</p>
                <h2 className="font-serif font-black text-3xl sm:text-4xl text-[#121212]">More from the Vault</h2>
              </div>
              <Link to="/tutorials" data-testid="related-see-all" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#121212] hover:text-[#E0218A] transition-colors">
                See all tutorials <ArrowRight size={15} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/tutorials/${r.slug || r.id}`}
                  data-testid={`related-tut-${r.id}`}
                  className="group bg-white border-2 border-[#121212] flex flex-col hover:-translate-y-1 transition-transform"
                >
                  <div className="aspect-video overflow-hidden border-b-2 border-[#121212] relative">
                    <img src={thumb(r.image_url, 600)} alt={r.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-[#121212]/0 group-hover:bg-[#121212]/20 transition-colors flex items-center justify-center">
                      <PlayCircle size={40} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0218A]">{r.level}</p>
                    <h3 className="font-serif font-black text-xl text-[#121212] mt-1 leading-tight">{r.title}</h3>
                    <p className="mt-2 text-[#121212]/60 text-sm inline-flex items-center gap-1.5"><Clock size={13} /> {r.duration}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

